import {
  getAdminV1ApplicationsAppId,
  getAdminV1ApplicationsAppIdHistory,
  getAdminV1ApplicationsAppIdNotes,
  getAdminV1BorrowerApplications,
  getAdminV1Conditions,
  getAdminV1PreapprovalBorrowerApplicationsAppIdEmailStatus,
  getAdminV1PreapprovalDocumentAppId,
  postAdminV1ApplicationsAppIdNotes,
  postAdminV1Conditions,
  postAdminV1ConditionsSync,
  postAdminV1PreapprovalDocumentAppIdGenerate,
  postAdminV1PreapprovalDocumentAppIdSend,
  putAdminV1ApplicationsAppIdNotesNoteId,
} from "@porchpass/api-client";
import type {
  DomainTaskStatusType,
  DtoAdminGeneratePreapprovalNoticeDocumentResp,
  DtoAdminGetApplicationResponse,
  DtoAdminGetPreapprovalEmailStatusResp,
  DtoAdminSearchBorrowerApplication,
  DtoCreateConditionRequest,
  DtoHistoryRecord,
  DtoSyncConditionRequest,
} from "@porchpass/api-client";
import {
  buildCreateConditionBody,
  mapApiConditionTag,
  mapBorrowerSearchResult,
  mapDtoCondition,
  mapDtoNote,
  mapSectionToTag,
  mapWorkspaceBootstrap,
} from "./mappers";
import type {
  ConditionSection,
  EmailRecipient,
  PreApprovalCondition,
  PreApprovalNote,
} from "./types";

const NOTES_PAGE_SIZE = 100;
const HISTORY_PAGE_SIZE = 100;

export interface CreateNoteResult {
  id: string;
  note: string;
}

function unexpectedStatus(status: number): Error {
  return new Error(`Unexpected API response (${status}).`);
}

export async function lookupApplication(
  externalId: string,
  signal?: AbortSignal,
): Promise<DtoAdminGetApplicationResponse> {
  const response = await getAdminV1ApplicationsAppId(externalId, { signal });
  if (response.status !== 200) {
    throw unexpectedStatus(response.status);
  }
  return mapWorkspaceBootstrap(response.data);
}

export async function searchBorrowers(
  query: string,
  signal?: AbortSignal,
): Promise<DtoAdminSearchBorrowerApplication[]> {
  const response = await getAdminV1BorrowerApplications(
    { search: query.trim() },
    { signal },
  );

  if (response.status !== 200) {
    throw unexpectedStatus(response.status);
  }

  return (response.data ?? [])
    .map(mapBorrowerSearchResult)
    .filter(
      (result): result is DtoAdminSearchBorrowerApplication => result !== null,
    );
}

function normalizeEmail(email: string): string | null {
  const normalized = email.trim().toLowerCase();
  return normalized || null;
}

export function mapRecipients(
  response: DtoAdminGeneratePreapprovalNoticeDocumentResp,
): EmailRecipient[] {
  const recipients: EmailRecipient[] = [];
  const seen = new Set<string>();

  const addRecipients = (
    emails: string[] | undefined,
    group: EmailRecipient["group"],
    label: string,
  ) => {
    for (const email of emails ?? []) {
      const normalized = normalizeEmail(email);
      if (!normalized || seen.has(normalized)) {
        continue;
      }
      seen.add(normalized);
      recipients.push({
        id: `${group}:${normalized}`,
        email: normalized,
        label,
        group,
      });
    }
  };

  addRecipients(response.borrower_recipients, "borrower", "Borrower");
  addRecipients(response.dealer_recipients, "dealer", "Dealer");

  return recipients;
}

export async function generateLetter(
  losApplicationId: string,
): Promise<EmailRecipient[]> {
  const response =
    await postAdminV1PreapprovalDocumentAppIdGenerate(losApplicationId);
  if (response.status !== 200) {
    throw unexpectedStatus(response.status);
  }
  return mapRecipients(response.data);
}

export async function getEmailStatus(
  losApplicationId: string,
): Promise<DtoAdminGetPreapprovalEmailStatusResp | null> {
  const response =
    await getAdminV1PreapprovalBorrowerApplicationsAppIdEmailStatus(
      losApplicationId,
    );
  if (response.status === 204) {
    return null;
  }
  if (response.status !== 200) {
    throw unexpectedStatus(response.status);
  }
  return response.data;
}

export async function getPreapprovalDocumentPdf(
  losApplicationId: string,
  signal?: AbortSignal,
): Promise<Blob> {
  const response = await getAdminV1PreapprovalDocumentAppId(
    losApplicationId,
    { inline: true },
    { signal },
  );
  if (response.status !== 200) {
    throw unexpectedStatus(response.status);
  }
  return response.data;
}

export async function sendPreApproval(
  losApplicationId: string,
  recipients: EmailRecipient[],
): Promise<void> {
  const borrowerRecipients = recipients
    .filter((recipient) => recipient.group === "borrower")
    .map((recipient) => recipient.email);
  const dealerRecipients = recipients
    .filter((recipient) => recipient.group === "dealer")
    .map((recipient) => recipient.email);

  const response = await postAdminV1PreapprovalDocumentAppIdSend(
    losApplicationId,
    {
      borrower_recipients: borrowerRecipients,
      dealer_recipients: dealerRecipients,
    },
  );
  if (response.status !== 200) {
    throw unexpectedStatus(response.status);
  }
}

export async function createNote(
  externalId: string,
  content: string,
): Promise<CreateNoteResult> {
  const trimmed = content.trim();
  if (!trimmed) {
    throw new Error("Note content is required.");
  }

  const response = await postAdminV1ApplicationsAppIdNotes(externalId, {
    note: trimmed,
  });
  if (response.status !== 201) {
    throw unexpectedStatus(response.status);
  }
  const id = response.data.id?.trim();
  if (!id) {
    throw new Error("Create note response is missing a note ID.");
  }
  return {
    id,
    note: response.data.note ?? trimmed,
  };
}

export async function getActivityHistory(
  externalId: string,
  signal?: AbortSignal,
): Promise<DtoHistoryRecord[]> {
  const allEvents: DtoHistoryRecord[] = [];
  let offset = 0;
  let total = Infinity;

  while (offset < total) {
    const response = await getAdminV1ApplicationsAppIdHistory(
      externalId,
      {
        order: "desc",
        limit: HISTORY_PAGE_SIZE,
        offset,
      },
      { signal },
    );
    if (response.status !== 200) {
      throw unexpectedStatus(response.status);
    }

    total = response.data.total ?? 0;
    const pageEvents = response.data.history ?? [];
    allEvents.push(...pageEvents);
    offset += HISTORY_PAGE_SIZE;

    if (pageEvents.length === 0) {
      break;
    }
  }

  return allEvents;
}

export async function getNotes(
  externalId: string,
  signal?: AbortSignal,
): Promise<PreApprovalNote[]> {
  const allNotes: PreApprovalNote[] = [];
  let offset = 0;
  let total = Infinity;

  while (offset < total) {
    const response = await getAdminV1ApplicationsAppIdNotes(
      externalId,
      {
        sort: "updated_at",
        order: "desc",
        limit: NOTES_PAGE_SIZE,
        offset,
      },
      { signal },
    );
    if (response.status !== 200) {
      throw unexpectedStatus(response.status);
    }

    total = response.data.total ?? 0;
    const pageNotes = (response.data.notes ?? []).map((note) =>
      mapDtoNote(externalId, note),
    );
    allNotes.push(...pageNotes);
    offset += NOTES_PAGE_SIZE;

    if (pageNotes.length === 0) {
      break;
    }
  }

  return allNotes;
}

export async function getNotesCount(
  externalId: string,
  signal?: AbortSignal,
): Promise<number> {
  const response = await getAdminV1ApplicationsAppIdNotes(
    externalId,
    { limit: 1, offset: 0 },
    { signal },
  );
  if (response.status !== 200) {
    throw unexpectedStatus(response.status);
  }
  return response.data.total ?? 0;
}

export async function updateNote(
  externalId: string,
  noteId: string,
  content: string,
): Promise<PreApprovalNote> {
  const trimmed = content.trim();
  if (!trimmed) {
    throw new Error("Note content is required.");
  }

  const response = await putAdminV1ApplicationsAppIdNotesNoteId(
    externalId,
    noteId,
    { note: trimmed },
  );
  if (response.status !== 200) {
    throw unexpectedStatus(response.status);
  }
  return mapDtoNote(externalId, response.data);
}

export async function getConditions(
  externalId: string,
  section: ConditionSection,
  signal?: AbortSignal,
): Promise<PreApprovalCondition[]> {
  const response = await getAdminV1Conditions(
    {
      application_id: externalId,
      tag: [mapSectionToTag(section)],
    },
    { signal },
  );
  if (response.status !== 200) {
    throw unexpectedStatus(response.status);
  }
  return (response.data.conditions ?? []).map((condition) =>
    mapDtoCondition(externalId, condition),
  );
}

export async function getOptionalConditions(
  externalId: string,
  signal?: AbortSignal,
): Promise<PreApprovalCondition[]> {
  const response = await getAdminV1Conditions(
    {
      application_id: externalId,
      status: ["cancelled"],
    },
    { signal },
  );
  if (response.status !== 200) {
    throw unexpectedStatus(response.status);
  }
  return (response.data.conditions ?? []).map((condition) =>
    mapDtoCondition(externalId, condition),
  );
}

export async function addCondition(
  externalId: string,
  section: ConditionSection,
  payload: Pick<
    DtoCreateConditionRequest,
    "title" | "internal_description" | "external_description"
  >,
): Promise<PreApprovalCondition> {
  const response = await postAdminV1Conditions(
    buildCreateConditionBody(externalId, section, payload),
  );
  if (response.status !== 201) {
    throw unexpectedStatus(response.status);
  }
  const taskId = response.data.id?.trim();
  if (!taskId) {
    throw new Error("Create condition response is missing a DigiFi task ID.");
  }
  return mapDtoCondition(externalId, {
    id: taskId,
    title: response.data.title,
    internal_description: response.data.internal_description,
    external_description: response.data.external_description,
    tag: mapApiConditionTag(response.data.tag),
    status: response.data.status,
    updated_at: new Date().toISOString(),
  });
}

async function syncConditionUpdates(
  externalId: string,
  updates: Array<{
    losId: string;
    status?: DomainTaskStatusType;
    tag?: DtoSyncConditionRequest["tag"];
    title?: string;
    internal_description?: string;
    external_description?: string;
  }>,
): Promise<void> {
  const response = await postAdminV1ConditionsSync({
    application_id: externalId,
    conditions: updates.map((update) => ({
      los_id: update.losId,
      ...(update.status ? { status: update.status } : {}),
      ...(update.tag ? { tag: update.tag } : {}),
      ...(update.title ? { title: update.title } : {}),
      ...(update.internal_description !== undefined
        ? { internal_description: update.internal_description }
        : {}),
      ...(update.external_description !== undefined
        ? { external_description: update.external_description }
        : {}),
    })),
  });
  if (response.status !== 200) {
    throw unexpectedStatus(response.status);
  }
  const failed = response.data.results?.find((result) => !result.success);
  if (failed) {
    throw new Error(failed.error || "Unable to sync condition changes.");
  }
}

export async function updateConditionStatus(
  externalId: string,
  condition: PreApprovalCondition,
  status: DomainTaskStatusType,
): Promise<PreApprovalCondition> {
  const losId = condition.id;
  if (!losId) {
    throw new Error("Condition is missing a DigiFi task ID.");
  }
  await syncConditionUpdates(externalId, [{ losId, status }]);
  return {
    ...condition,
    status,
    updated_at: new Date().toISOString(),
    digifi_synced: true,
  };
}

export type UpdateConditionPayload = Pick<
  DtoSyncConditionRequest,
  "title" | "internal_description" | "external_description" | "status"
> & {
  status: DomainTaskStatusType;
};

export async function updateCondition(
  externalId: string,
  condition: PreApprovalCondition,
  payload: UpdateConditionPayload,
): Promise<PreApprovalCondition> {
  const losId = condition.id;
  if (!losId) {
    throw new Error("Condition is missing a DigiFi task ID.");
  }
  await syncConditionUpdates(externalId, [
    {
      losId,
      title: payload.title,
      internal_description: payload.internal_description,
      external_description: payload.external_description,
      status: payload.status,
    },
  ]);
  return {
    ...condition,
    title: payload.title,
    internal_description: payload.internal_description?.trim() || undefined,
    external_description: payload.external_description?.trim() || undefined,
    status: payload.status,
    updated_at: new Date().toISOString(),
    digifi_synced: true,
  };
}

export async function removeFromLetter(
  externalId: string,
  condition: PreApprovalCondition,
): Promise<PreApprovalCondition> {
  return updateConditionStatus(externalId, condition, "cancelled");
}

export async function reactivateOptionalCondition(
  externalId: string,
  condition: PreApprovalCondition,
  section: ConditionSection,
): Promise<PreApprovalCondition> {
  const losId = condition.id;
  if (!losId) {
    throw new Error("Condition is missing a DigiFi task ID.");
  }
  await syncConditionUpdates(externalId, [
    {
      losId,
      status: "not_done",
      tag: mapSectionToTag(section),
    },
  ]);
  return {
    ...condition,
    section,
    status: "not_done",
    updated_at: new Date().toISOString(),
    digifi_synced: true,
  };
}

async function fetchAllConditions(
  externalId: string,
  signal?: AbortSignal,
): Promise<PreApprovalCondition[]> {
  const response = await getAdminV1Conditions(
    { application_id: externalId },
    { signal },
  );
  if (response.status !== 200) {
    throw unexpectedStatus(response.status);
  }
  return (response.data.conditions ?? []).map((condition) =>
    mapDtoCondition(externalId, condition),
  );
}

export async function syncToDigiFi(
  externalId: string,
): Promise<PreApprovalCondition[]> {
  const conditions = await fetchAllConditions(externalId);
  if (conditions.length === 0) {
    return [];
  }

  const updates = conditions.flatMap((condition) => {
    const losId = condition.id;
    if (!losId) {
      return [];
    }
    return [
      {
        losId,
        status: condition.status,
        tag: mapSectionToTag(condition.section),
      },
    ];
  });

  if (updates.length === 0) {
    return conditions;
  }

  await syncConditionUpdates(externalId, updates);
  return conditions.map((condition) => ({
    ...condition,
    digifi_synced: true,
  }));
}

export async function getAllConditionsForCounts(
  externalId: string,
  signal?: AbortSignal,
): Promise<PreApprovalCondition[]> {
  return fetchAllConditions(externalId, signal);
}
